import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useEffect } from "react";
import { format, startOfMonth } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import useIsMobile from "../../../hooks/useIsMobile";
import { useAPIGetKartuStokMinyak } from "../../kartu-stok-minyak/api/api-get-kartu-stok-minyak";

const formatLiter = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

const dashIfZero = (value) => (Number(value) > 0 ? formatLiter(value) : "-");

/**
 * The kartu stok of one month for a department + jenis minyak, to look at while analysing a
 * permintaan minyak. Only viewing: no add, edit, delete or signature.
 * tanggal: a date of the month to show.
 */
const KartuStokViewDialog = (props) => {
  const { visible, onHide, department, jenisMinyak, tanggal } = props;

  const isMobile = useIsMobile();

  const { loading, listData, saldoAkhir, totalMasuk, totalKeluar, fetchListData } =
    useAPIGetKartuStokMinyak();

  const parsed = tanggal ? new Date(String(tanggal).replace(" ", "T")) : new Date();
  const bulan = startOfMonth(isNaN(parsed) ? new Date() : parsed);

  useEffect(() => {
    if (visible && department?.id && jenisMinyak?.id) {
      fetchListData({
        department_id: department.id,
        jenis_minyak_id: jenisMinyak.id,
        bulan,
      });
    }
  }, [visible, department?.id, jenisMinyak?.id, tanggal]);

  const alatBeratBody = (row) =>
    row.data_alat_berat
      ? [row.data_alat_berat.kode_unit, row.data_alat_berat.nama]
          .filter(Boolean)
          .join(" - ")
      : "-";

  const dateText = (row) =>
    row.tanggal_waktu
      ? format(new Date(String(row.tanggal_waktu).replace(" ", "T")), "dd/MM/yyyy HH:mm")
      : "-";

  const operatorText = (row) => row.data_operator?.nama ?? row.operator_nama ?? "";

  // phone: one card per line, like the kartu stok page
  const mobileBody = (row) => {
    const isMasuk = row.jenis_mutasi === "masuk";

    return (
      <div className="col-start-start w-full gap-1 pb-1">
        <div className="row-between-center w-full">
          <Tag
            value={isMasuk ? "Masuk" : "Keluar"}
            severity={isMasuk ? "success" : "warning"}
          />
          <span className="text-sm font-semibold italic text-slate-400">
            {dateText(row)}
          </span>
        </div>
        <span className="pt-1 font-bold text-slate-500">
          {isMasuk
            ? row.data_permintaan_minyak?.nomor
              ? `Ref. ${row.data_permintaan_minyak.nomor}`
              : "Stok masuk"
            : alatBeratBody(row)}
        </span>
        {!isMasuk && operatorText(row) && (
          <span className="text-sm text-slate-500">
            <i className="pi pi-user mr-2 text-slate-400" />
            {operatorText(row)}
          </span>
        )}
        <span className="text-sm text-slate-500">
          {isMasuk ? "Masuk" : "Keluar"}{" "}
          <strong>{formatLiter(isMasuk ? row.masuk : row.keluar)} L</strong>
          {" · "}Sisa <strong>{formatLiter(row.sisa)} L</strong>
        </span>
        {row.keterangan && (
          <span className="text-sm text-slate-500">
            <i className="pi pi-comment mr-2 text-slate-400" />
            {row.keterangan}
          </span>
        )}
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "60rem" }}
      breakpoints={{ "960px": "90vw", "641px": "100vw" }}
      header="Kartu Stok Minyak"
      modal
      onHide={onHide}
      contentClassName="py-4"
      pt={{ header: { className: "py-4" } }}
    >
      <div className="col-start-start relative w-full gap-3">
        <div className="col-start-start gap-1">
          <span className="font-bold text-slate-600">
            {[department?.nama, jenisMinyak?.nama].filter(Boolean).join(" · ")}
          </span>
          <span className="text-sm text-slate-400">
            {format(bulan, "MMMM yyyy", { locale: idLocale })} · TRAN-DOC-001
          </span>
        </div>

        {isMobile ? (
          <>
            <DataTable
              value={listData}
              showGridlines
              stripedRows
              className="w-full"
              emptyMessage="Belum ada transaksi bulan ini."
            >
              <Column header="Kartu Stok" body={mobileBody} />
            </DataTable>
            {listData.length > 0 && (
              <div className="col-start-start w-full gap-1 rounded bg-indigo-50 px-3 py-2">
                <span className="font-bold text-slate-500">Total bulan ini</span>
                <div className="row-between-center w-full text-sm text-slate-500">
                  <span>
                    Masuk <strong>{formatLiter(totalMasuk)} L</strong>
                  </span>
                  <span>
                    Keluar <strong>{formatLiter(totalKeluar)} L</strong>
                  </span>
                </div>
                <div className="row-between-center w-full">
                  <span className="text-sm text-slate-500">Saldo akhir</span>
                  <span className="font-bold text-emerald-600">
                    {formatLiter(saldoAkhir)} L
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
        <DataTable
          value={listData}
          size="small"
          showGridlines
          stripedRows
          scrollable
          className="w-full"
          emptyMessage="Belum ada transaksi bulan ini."
        >
          <Column header="No" field="no" />
          <Column
            header="Tgl / Waktu"
            body={dateText}
          />
          <Column header="Alat Berat" body={alatBeratBody} />
          <Column header="Operator" body={(row) => operatorText(row) || "-"} />
          <Column
            align="right"
            header="Masuk (L)"
            body={(row) => dashIfZero(row.masuk)}
            footer={formatLiter(totalMasuk)}
          />
          <Column
            align="right"
            header="Keluar (L)"
            body={(row) => dashIfZero(row.keluar)}
            footer={formatLiter(totalKeluar)}
          />
          <Column
            align="right"
            header="Sisa (L)"
            body={(row) => <b>{formatLiter(row.sisa)}</b>}
            footer={saldoAkhir === null ? "" : formatLiter(saldoAkhir)}
          />
          <Column header="Keterangan" body={(row) => row.keterangan || "-"} />
        </DataTable>
        )}

        {loading && (
          <div className="col-center-center absolute inset-0 z-100 gap-2 rounded bg-black/20">
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default KartuStokViewDialog;
